import React, { useState, useRef, useEffect } from 'react'
import { ChromePicker } from 'react-color'
import ColorWheel from './ColorWheel'

const EYEDROPPER_SUPPORTED = typeof window !== 'undefined' && 'EyeDropper' in window

const tabButtonStyle = (active) => ({
  flex: 1,
  padding: '4px 8px',
  fontSize: 11,
  fontWeight: active ? 'bold' : 'normal',
  color: active ? '#4a90d9' : '#666',
  background: active ? '#eaf2fb' : 'transparent',
  border: '1px solid #ddd',
  borderRadius: 4,
  cursor: 'pointer',
})

// The Grid/Wheel toggle + whichever picker is active + the eyedropper button.
// Shared by the standalone popover below and by any caller that already has
// its own popover open (e.g. the stroke control) and just wants this body
// embedded inline, without a second swatch-button layer in the way.
function ColorPickerBody({ color, onChange, onPicked }) {
  const [mode, setMode] = useState('grid')

  const handleEyedropper = async () => {
    if (!EYEDROPPER_SUPPORTED) return
    try {
      const eyeDropper = new window.EyeDropper()
      const result = await eyeDropper.open()
      onChange(result.sRGBHex)
      if (onPicked) onPicked()
    } catch (err) {
      // User pressed Escape / cancelled the pick -- nothing to do.
    }
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
        <button type="button" style={tabButtonStyle(mode === 'grid')} onClick={() => setMode('grid')}>Grid</button>
        <button type="button" style={tabButtonStyle(mode === 'wheel')} onClick={() => setMode('wheel')}>Wheel</button>
        {EYEDROPPER_SUPPORTED && (
          <button
            type="button"
            title="Pick color from screen"
            onClick={handleEyedropper}
            style={{ ...tabButtonStyle(false), flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16">
              <path fill="currentColor" d="M13.5 1.5a2.1 2.1 0 0 0-3 0L9 3l-.7-.7a.5.5 0 0 0-.7.7l.7.7-6 6a1 1 0 0 0-.28.52l-.8 3.6a.5.5 0 0 0 .6.6l3.6-.8a1 1 0 0 0 .52-.28l6-6 .7.7a.5.5 0 0 0 .7-.7l-.7-.7 1.5-1.5a2.1 2.1 0 0 0 0-3zM3.4 12.1l5.6-5.6 1.5 1.5-5.6 5.6-1.9.4z" />
            </svg>
          </button>
        )}
      </div>
      {mode === 'grid' ? (
        <ChromePicker
          color={color || '#000000'}
          onChange={(c) => onChange(c.hex)}
          disableAlpha={true}
        />
      ) : (
        <ColorWheel color={color || '#000000'} onChange={onChange} />
      )}
    </>
  )
}

/**
 * ColorPickerControl - shared color-swatch button + popover used for both
 * text fill color and text stroke color. Bundles three ways to pick a color:
 *   - Grid: the existing square saturation/value picker (ChromePicker)
 *   - Wheel: a circular hue/saturation wheel (see ColorWheel.jsx)
 *   - Eyedropper: native browser EyeDropper API, samples a color from
 *     anywhere on screen (not just the canvas)
 *
 * `styles` is the caller's own CSS module object (colorControl/colorButton/
 * colorBar/colorPickerPopup classes already exist in FloatingToolbar's scss)
 * -- passed down so this control visually matches without duplicating CSS.
 *
 * Pass `inline` when the caller already has its own popover open (e.g. the
 * stroke control) -- renders just the picker body, no swatch button/popover
 * wrapper of its own.
 */
export default function ColorPickerControl({ color, onChange, icon, title, styles, inline = false }) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)

  useEffect(() => {
    if (inline) return
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [inline])

  if (inline) {
    return <ColorPickerBody color={color} onChange={onChange} />
  }

  return (
    <div className={styles.colorControl} ref={containerRef}>
      <button type="button" className={styles.colorButton} onClick={() => setOpen(!open)} title={title}>
        {icon}
        <span className={styles.colorBar} style={{ backgroundColor: color || '#000000' }} />
      </button>
      {open && (
        <div className={styles.colorPickerPopup}>
          <ColorPickerBody color={color} onChange={onChange} onPicked={() => setOpen(false)} />
        </div>
      )}
    </div>
  )
}
